// client/src/components/MainGame.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
FaShieldAlt, FaFutbol, FaExchangeAlt, FaFire, FaBolt, FaChartLine,
FaInfoCircle, FaGlobe, FaTrophy, FaMedal, FaLock, FaCheckCircle,
FaUserCircle, FaSignOutAlt, FaUsers, FaClock, FaArrowRight, FaStar,
FaBell, FaQuestionCircle, FaCrown, FaSyncAlt, FaBars, FaRunning, FaCompress,
FaWifi, FaExclamationTriangle, FaTimes, FaCalendarAlt, FaHandshake
} from 'react-icons/fa';
import Card from './Card';
import LiveMatch from './LiveMatch';
import StandingsPage from './StandingsPage';
import ProfilePage from './ProfilePage';
import HallOfFame from './HallOfFame';
import SquadTacticalView from './SquadTacticalView';
import { useSocket } from '../context/SocketContext';
import {
fetchUserTeam, getProfile, getUserDailyStats, fetchStandings,
resetDomesticSeason, resetContinentalSeason,
fetchNotifications, markNotificationRead, markAllNotificationsRead, deleteNotification,
fetchQualifiedUsers, fetchCurrentSeason,
fetchUserSquad, saveUserSquad,
setCaptain as setCaptainApi,
updateUserSettings  // ✅ NEW: استيراد دالة حفظ الإعدادات
} from '../services/api';
import StatisticsPage from './StatisticsPage';
import TutorialManager from './TutorialManager';
import SeasonInfo from './SeasonInfo';
import LanguageSelector from './LanguageSelector';
import { requestNotificationPermission, listenForNotifications } from '../services/firebase';

// ✅ NEW: استيراد دالة تسجيل الأخطاء
import { reportError } from '../services/errorReporter';

// ========== Playing Styles ==========
const PLAYING_STYLES = {
  balanced: { name: 'Balanced', attackBonus: 0, defenseBonus: 0, icon: '↔️', description: 'Balanced attack & defense' },
  long_ball: { name: 'Long Balls', attackBonus: 0.20, defenseBonus: -0.10, icon: '⚽', description: 'High risk, high reward' },
  short_pass: { name: 'Short Passing', attackBonus: 0.10, defenseBonus: 0.05, icon: '🎯', description: 'Possession based' }
};

// ========== Power calculation functions ==========
function getPlayerPower(player) {
  if (!player || typeof player !== 'object') return 0;
  const attack = Number(player.attack) || 0;
  const defense = Number(player.defense) || 0;
  const speed = Number(player.speed) || 0;
  const accuracy = Number(player.accuracy) || 0;
  const strength = Number(player.strength) || 50;
  const tactics = Number(player.tactics) || 50;
  const { position, special_ability } = player;
  let power = 0;
  if (position === 'Striker') {
    power = (speed * 0.4 + accuracy * 0.4 + attack * 0.3 + defense * 0.2 + strength * 0.4 + tactics * 0.3) / 2.0;
  } else if (position === 'Defender') {
    power = (defense * 0.4 + speed * 0.3 + strength * 0.5 + tactics * 0.3 + attack * 0.2 + accuracy * 0.2) / 1.9;
  } else if (position === 'Midfielder') {
    power = (speed * 0.3 + defense * 0.3 + attack * 0.3 + accuracy * 0.3 + strength * 0.4 + tactics * 0.5) / 2.1;
  } else if (position === 'Goalkeeper') {
    power = (attack + defense + speed + accuracy + strength + tactics) / 6;
  } else {
    power = (attack + defense + speed + accuracy + strength + tactics) / 6;
  }
  if (special_ability === 'Playmaker') power *= 1.05;
  else if (special_ability === 'Super Scorer' && position === 'Striker') power *= 1.15;
  else if (special_ability === 'Super Saver' && position === 'Goalkeeper') power *= 1.15;
  else if (special_ability === 'Speed Runner') power *= 1.10;
  return Math.min(100, Math.max(0, power));
}

function calculateBaseTeamStrength(players) {
  const validPlayers = (players || []).filter(p => p && p.position);
  const outfield = validPlayers.filter(p => p.position !== 'Goalkeeper');
  const goalkeeper = validPlayers.find(p => p.position === 'Goalkeeper');
  if (outfield.length === 0 || !goalkeeper) return 0;
  const outfieldSum = outfield.reduce((sum, p) => sum + getPlayerPower(p), 0);
  const outfieldAvg = outfieldSum / outfield.length;
  const gkPower = getPlayerPower(goalkeeper);
  return Math.min(100, Math.max(0, (outfieldAvg * 0.9 + gkPower * 0.1)));
}

const FORMATION_BONUS = {
  counter: { striker: 2, midfielder: 1, defender: -2 },
  aggressive: { striker: 1, midfielder: 2, defender: -1 },
  traditionalAttack: { striker: 0, midfielder: 1, defender: 0 },
  traditionalDefense: { striker: 1, midfielder: 1, defender: 2 },
  highPress: { striker: 2, midfielder: 2, defender: -1, staminaCost: 1.3, yellowCardProb: 1.2 },
  advancedDefense: { striker: 0, midfielder: 1, defender: 3, interceptionBonus: 1.2, counterAttackPenalty: 0.8 }
};

function calculateFormationPower(players, formationId, playingStyle = 'balanced') {
  const validPlayers = (players || []).filter(p => p && p.position);
  const strikers = validPlayers.filter(p => p.position === 'Striker');
  const midfielders = validPlayers.filter(p => p.position === 'Midfielder');
  const defenders = validPlayers.filter(p => p.position === 'Defender');
  const goalkeeper = validPlayers.find(p => p.position === 'Goalkeeper');
  const bonus = FORMATION_BONUS[formationId] || FORMATION_BONUS.traditionalAttack;
  const style = PLAYING_STYLES[playingStyle] || PLAYING_STYLES.balanced;
  const adjStriker = strikers.map(p => getPlayerPower(p) + (bonus.striker || 0));
  const adjMid = midfielders.map(p => getPlayerPower(p) + (bonus.midfielder || 0));
  const adjDef = defenders.map(p => getPlayerPower(p) + (bonus.defender || 0));
  const avgStriker = adjStriker.length ? adjStriker.reduce((a,b)=>a+b,0)/adjStriker.length : 0;
  const avgMid = adjMid.length ? adjMid.reduce((a,b)=>a+b,0)/adjMid.length : 0;
  const avgDef = adjDef.length ? adjDef.reduce((a,b)=>a+b,0)/adjDef.length : 0;
  const gkPower = goalkeeper ? getPlayerPower(goalkeeper) : 0;
  let formationPower = 0;
  if (formationId === 'counter' || formationId === 'aggressive') {
    const attackMid = (avgStriker + avgMid) / 2;
    formationPower = attackMid * 0.7 + avgDef * 0.2 + gkPower * 0.1;
  } else if (formationId === 'traditionalAttack') {
    const attackMid = (avgStriker + avgMid) / 2;
    formationPower = attackMid * 0.5 + avgDef * 0.4 + gkPower * 0.1;
  } else if (formationId === 'traditionalDefense') {
    const attackMid = (avgStriker + avgMid) / 2;
    formationPower = attackMid * 0.4 + avgDef * 0.5 + gkPower * 0.1;
  } else if (formationId === 'highPress') {
    const avgStrength = validPlayers.reduce((s,p) => s + (p.strength || 50), 0) / validPlayers.length;
    const avgSpeed = validPlayers.reduce((s,p) => s + (p.speed || 50), 0) / validPlayers.length;
    formationPower = (avgStriker * 0.3 + avgMid * 0.35 + avgDef * 0.2 + avgStrength * 0.1 + avgSpeed * 0.05);
  } else if (formationId === 'advancedDefense') {
    formationPower = (avgDef * 0.7 + avgMid * 0.2 + gkPower * 0.1);
  }
  formationPower = formationPower * (1 + style.attackBonus + (style.defenseBonus || 0));
  return Math.min(100, Math.max(0, formationPower));
}

function calculateFinalTeamStrength(baseStrength, formationPower, captain) {
  const combined = (baseStrength + formationPower) / 2;
  let captainBonus = 0;
  if (captain) {
    captainBonus = combined * 0.05;
    if (captain.special_ability === 'Playmaker') captainBonus += combined * 0.05;
    else if (captain.special_ability === 'Super Scorer' && captain.position === 'Striker') captainBonus += combined * 0.10;
    else if (captain.special_ability === 'Super Saver' && captain.position === 'Goalkeeper') captainBonus += combined * 0.10;
    else if (captain.special_ability === 'Speed Runner') captainBonus += combined * 0.07;
  }
  return Math.min(100, Math.max(0, combined + captainBonus));
}

const formationExplanations = {
  counter: { name: 'Counter (2-2-1)', bonuses: '+30% Striker, +20% Midfielder, -70% Defender', desc: 'Swift transitions, deadly on break.', detailed: `• Strikers: +2 power\n• Midfielders: +1 power\n• Defenders: -2 power\n• Formula: 70% attack, 20% defense, 10% GK\n` },
  aggressive: { name: 'Aggressive (2-1-2)', bonuses: '+25% Striker, -50% Defender', desc: 'High pressure, all‑out attack.', detailed: `• Strikers: +1 power\n• Midfielders: +2 power\n• Defenders: -1 power\n• Formula: 70% attack, 20% defense, 10% GK\n` },
  traditionalAttack: { name: 'Traditional Attack (2-2-1)', bonuses: '+25% Star Strikers, +25% Star Midfielders', desc: 'Balanced attacking style.', detailed: `• Strikers: no change; stars +0.25\n• Midfielders: +1; stars +0.25\n• Defenders: unchanged\n• Formula: 50% attack, 40% defense, 10% GK\n` },
  traditionalDefense: { name: 'Traditional Defense (3-1-1)', bonuses: '+25% Star Defenders, +25% Star Midfielders', desc: 'Solid defensive organisation.', detailed: `• Defenders: +2; stars +0.25\n• Midfielders: +1; stars +0.25\n• Strikers: +1\n• Formula: 40% attack, 50% defense, 10% GK\n` },
  highPress: { name: 'High Press (4-3-3)', bonuses: '+20% Attack, +30% Midfield, High stamina drain', desc: 'Continuous pressing, quick recovery.', detailed: `• Strikers: +2\n• Midfielders: +2\n• Defenders: -1\n• Increased yellow card probability\n` },
  advancedDefense: { name: 'Advanced Defense (5-3-2)', bonuses: '+30% Defense, Counter attack penalty', desc: 'High defensive line with advanced coverage.', detailed: `• Defenders: +3\n• Midfielders: +1\n• Strikers: 0\n• Reduced counter-attack efficiency\n` }
};

const formations = [
  { id: 'aggressive', nameKey: 'formation.aggressive', short: '2-1-2', icon: <FaFire className="text-red-400" />, bonusesKeys: ['+25% Striker', '-50% Defender'], descKey: 'formation.aggressiveDesc' },
  { id: 'counter', nameKey: 'formation.counter', short: '2-2-1', icon: <FaBolt className="text-yellow-400" />, bonusesKeys: ['+30% Striker', '+20% Midfielder', '-70% Defender'], descKey: 'formation.counterDesc' },
  { id: 'traditionalAttack', nameKey: 'formation.traditionalAttack', short: '2-2-1', icon: <FaFutbol className="text-green-400" />, bonusesKeys: ['+25% Star Strikers', '+25% Star Midfielders'], descKey: 'formation.traditionalAttackDesc' },
  { id: 'traditionalDefense', nameKey: 'formation.traditionalDefense', short: '3-1-1', icon: <FaShieldAlt className="text-blue-400" />, bonusesKeys: ['+25% Star Defenders', '+25% Star Midfielders'], descKey: 'formation.traditionalDefenseDesc' },
  { id: 'highPress', nameKey: 'formation.highPress', short: '4-3-3', icon: <FaRunning className="text-orange-400" />, bonusesKeys: ['+20% Attack', '+30% Midfield', 'High press'], descKey: 'formation.highPressDesc' },
  { id: 'advancedDefense', nameKey: 'formation.advancedDefense', short: '5-3-2', icon: <FaCompress className="text-cyan-400" />, bonusesKeys: ['+30% Defense', 'Counter penalty'], descKey: 'formation.advancedDefenseDesc' }
];

const calculateTeamAverage = (team) => {
  if (!team || team.length === 0) return { attack: 0, defense: 0, speed: 0, accuracy: 0, strength: 0, tactics: 0 };
  const validPlayers = team.filter(p => p && typeof p === 'object');
  if (validPlayers.length === 0) return { attack: 0, defense: 0, speed: 0, accuracy: 0, strength: 0, tactics: 0 };
  const totals = validPlayers.reduce((acc, p) => {
    acc.attack += Number(p.attack) || 0;
    acc.defense += Number(p.defense) || 0;
    acc.speed += Number(p.speed) || 0;
    acc.accuracy += Number(p.accuracy) || 0;
    acc.strength += Number(p.strength) || 50;
    acc.tactics += Number(p.tactics) || 50;
    return acc;
  }, { attack: 0, defense: 0, speed: 0, accuracy: 0, strength: 0, tactics: 0 });
  const count = validPlayers.length;
  return {
    attack: Math.round(totals.attack / count),
    defense: Math.round(totals.defense / count),
    speed: Math.round(totals.speed / count),
    accuracy: Math.round(totals.accuracy / count),
    strength: Math.round(totals.strength / count),
    tactics: Math.round(totals.tactics / count),
  };
};

// ============================================================
//  StadiumPitchView – توزيع مختلف حسب الشاشة
//  كبير: من اليمين إلى اليسار  |  صغير: من الأسفل إلى الأعلى (طولي)
// ============================================================
const StadiumPitchView = React.memo(({ team, formation, isMobile = false }) => {
  const { t } = useTranslation();
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const getPlayerImage = (position, rarity) => {
    const posMap = { Goalkeeper: 'goalkeeper', Defender: 'defender', Midfielder: 'midfielder', Striker: 'striker' };
    const base = posMap[position] || 'player';
    if (rarity === 'gold' || rarity === 'silver' || rarity === 'bronze') {
      return `/img/${rarity}_${base}.jpg`;
    }
    return `/img/${base}.jpg`;
  };

  const getPositionsForSquad = useCallback((squad) => {
    const groups = { Goalkeeper: [], Defender: [], Midfielder: [], Striker: [] };
    squad.forEach(p => {
      if (groups[p.position]) groups[p.position].push(p);
    });

    const coords = [];

    if (isMobile) {
      const offsetX = -8;
      const distributeSymmetric = (players, yPos) => {
        const count = players.length;
        if (count === 0) return;
        if (count === 1) {
          coords.push({ player: players[0], x: 50 + offsetX, y: yPos });
        } else if (count === 2) {
          coords.push({ player: players[0], x: 35 + offsetX, y: yPos });
          coords.push({ player: players[1], x: 65 + offsetX, y: yPos });
        } else if (count === 3) {
          coords.push({ player: players[0], x: 25 + offsetX, y: yPos });
          coords.push({ player: players[1], x: 50 + offsetX, y: yPos });
          coords.push({ player: players[2], x: 75 + offsetX, y: yPos });
        } else if (count === 4) {
          coords.push({ player: players[0], x: 20 + offsetX, y: yPos });
          coords.push({ player: players[1], x: 40 + offsetX, y: yPos });
          coords.push({ player: players[2], x: 60 + offsetX, y: yPos });
          coords.push({ player: players[3], x: 80 + offsetX, y: yPos });
        } else if (count === 5) {
          coords.push({ player: players[0], x: 15 + offsetX, y: yPos });
          coords.push({ player: players[1], x: 32 + offsetX, y: yPos });
          coords.push({ player: players[2], x: 50 + offsetX, y: yPos });
          coords.push({ player: players[3], x: 68 + offsetX, y: yPos });
          coords.push({ player: players[4], x: 85 + offsetX, y: yPos });
        } else {
          const margin = 5;
          const pitchWidth = 100;
          const step = (pitchWidth - 2 * margin) / (count - 1);
          players.forEach((p, i) => {
            const x = margin + i * step + offsetX;
            coords.push({ player: p, x, y: yPos });
          });
        }
      };

      if (groups.Goalkeeper.length) {
        coords.push({ player: groups.Goalkeeper[0], x: 50 + offsetX, y: 85 });
      }
      distributeSymmetric(groups.Defender, 68);
      distributeSymmetric(groups.Midfielder, 45);
      distributeSymmetric(groups.Striker, 22);
    } else {
      const offsetY = -5;
      const offsetX = -10;
      const distributeVertical = (players, xPos) => {
        const count = players.length;
        if (count === 0) return;
        if (count === 1) {
          coords.push({ player: players[0], x: xPos + offsetX, y: 50 + offsetY });
        } else if (count === 2) {
          coords.push({ player: players[0], x: xPos + offsetX, y: 35 + offsetY });
          coords.push({ player: players[1], x: xPos + offsetX, y: 65 + offsetY });
        } else if (count === 3) {
          coords.push({ player: players[0], x: xPos + offsetX, y: 25 + offsetY });
          coords.push({ player: players[1], x: xPos + offsetX, y: 50 + offsetY });
          coords.push({ player: players[2], x: xPos + offsetX, y: 75 + offsetY });
        } else if (count === 4) {
          coords.push({ player: players[0], x: xPos + offsetX, y: 20 + offsetY });
          coords.push({ player: players[1], x: xPos + offsetX, y: 40 + offsetY });
          coords.push({ player: players[2], x: xPos + offsetX, y: 60 + offsetY });
          coords.push({ player: players[3], x: xPos + offsetX, y: 80 + offsetY });
        } else if (count === 5) {
          coords.push({ player: players[0], x: xPos + offsetX, y: 15 + offsetY });
          coords.push({ player: players[1], x: xPos + offsetX, y: 32 + offsetY });
          coords.push({ player: players[2], x: xPos + offsetX, y: 50 + offsetY });
          coords.push({ player: players[3], x: xPos + offsetX, y: 68 + offsetY });
          coords.push({ player: players[4], x: xPos + offsetX, y: 85 + offsetY });
        } else {
          const margin = 10;
          const pitchHeight = 80;
          const step = (pitchHeight - 2 * margin) / (count - 1);
          players.forEach((p, i) => {
            const y = margin + i * step + offsetY;
            coords.push({ player: p, x: xPos + offsetX, y });
          });
        }
      };

      if (groups.Goalkeeper.length) {
        coords.push({ player: groups.Goalkeeper[0], x: 92 + offsetX, y: 50 + offsetY });
      }
      distributeVertical(groups.Defender, 72);
      distributeVertical(groups.Midfielder, 50);
      distributeVertical(groups.Striker, 28);
    }

    return coords;
  }, [isMobile]);

  const layout = useMemo(() => {
    if (!team || team.length === 0) return { goalkeeper: null, defenders: [], midfielders: [], strikers: [] };
    const positioned = getPositionsForSquad(team);
    const result = { goalkeeper: null, defenders: [], midfielders: [], strikers: [] };
    positioned.forEach(item => {
      if (item.player.position === 'Goalkeeper') result.goalkeeper = item;
      else if (item.player.position === 'Defender') result.defenders.push(item);
      else if (item.player.position === 'Midfielder') result.midfielders.push(item);
      else if (item.player.position === 'Striker') result.strikers.push(item);
    });
    return result;
  }, [team, getPositionsForSquad]);

  const getJerseyNumber = (player) => {
    if (!player) return 1;
    return (Math.abs(player.id) % 99) + 1;
  };

  const PlayerAvatar = React.memo(({ player, x, y, isGoalkeeper = false }) => {
    if (!player) return null;
    const playerPower = Math.round(getPlayerPower(player));
    const number = getJerseyNumber(player);
    const imgSize = isMobile ? 'w-12 h-12 sm:w-14 sm:h-14' : 'w-16 h-16 md:w-20 md:h-20';
    const numberSize = isMobile ? 'text-[8px] sm:text-[10px]' : 'text-[10px] md:text-xs';
    const nameSize = isMobile ? 'text-[8px] sm:text-[9px]' : 'text-[10px] md:text-[11px]';
    const handleClick = () => setSelectedPlayer(player);

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.3, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.6, delay: (y / 100) * 0.5, type: 'spring', stiffness: 180 }}
        className="absolute z-10 flex flex-col items-center cursor-pointer"
        style={{
          left: `${x}%`,
          top: `${y}%`,
          transform: 'translate(-50%, -50%)'
        }}
        onClick={handleClick}
      >
        <div className="relative group">
          <div
            className={`relative ${imgSize} rounded-full border-[3px] shadow-lg transition-transform duration-200 group-hover:scale-110 group-hover:-translate-y-1 overflow-hidden flex items-center justify-center`}
            style={{
              borderColor: isGoalkeeper ? '#f1c40f' : '#e74c3c',
              background: isGoalkeeper
                ? 'linear-gradient(135deg, #f1c40f, #f39c12)'
                : 'linear-gradient(135deg, #e74c3c, #c0392b)',
            }}
          >
            <img
              src={getPlayerImage(player.position, player.rarity)}
              alt={player.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/img/player.jpg';
              }}
            />
            <div className={`absolute bottom-0 left-0 right-0 bg-black/60 text-white text-center ${numberSize} font-black py-0.5`}>
              {number}
            </div>
          </div>
          {player.is_captain && (
            <div className="absolute -top-2 -right-2 w-5 h-5 md:w-6 md:h-6 bg-yellow-400 rounded-full flex items-center justify-center shadow-md border-2 border-white/50">
              <span className="text-[8px] md:text-[9px] font-black text-black">C</span>
            </div>
          )}
        </div>
        <div className="mt-1 md:mt-1.5 bg-black/70 backdrop-blur-sm px-1.5 md:px-2 py-0.5 rounded-full shadow-md border border-white/15">
          <span className={`${nameSize} font-bold text-white whitespace-nowrap`}>
            {player.name}
          </span>
        </div>
      </motion.div>
    );
  });

  const closeModal = () => setSelectedPlayer(null);

  const aspectRatio = isMobile ? '3 / 4' : '16 / 9';
  const topMargin = isMobile ? '4%' : '6%';
  const bottomMargin = isMobile ? '10%' : '12%';
  const leftRightMargin = isMobile ? '4%' : '4%';

  return (
    <div className="mb-6 md:mb-10">
      <div
        className="relative w-full max-w-6xl mx-auto rounded-2xl overflow-hidden shadow-2xl"
        style={{
          aspectRatio: aspectRatio,
          background: 'linear-gradient(180deg, #0a1628 0%, #0d2137 30%, #102a45 60%, #0a1628 100%)',
        }}
      >
        <div className="absolute top-2 left-[8%] w-4 h-4 sm:w-6 sm:h-6 rounded-full bg-white/20 blur-[6px] sm:blur-[8px]" />
        <div className="absolute top-2 left-[8%] w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-yellow-100/80 shadow-[0_0_15px_5px_rgba(255,255,200,0.3)]" />
        <div className="absolute top-2 right-[8%] w-4 h-4 sm:w-6 sm:h-6 rounded-full bg-white/20 blur-[6px] sm:blur-[8px]" />
        <div className="absolute top-2 right-[8%] w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-yellow-100/80 shadow-[0_0_15px_5px_rgba(255,255,200,0.3)]" />
        <div className="absolute top-1 left-[30%] w-3 h-3 sm:w-5 sm:h-5 rounded-full bg-white/10 blur-[4px] sm:blur-[6px]" />
        <div className="absolute top-1 left-[30%] w-1.5 h-1.5 sm:w-2.5 sm:h-2.5 rounded-full bg-yellow-100/60 shadow-[0_0_10px_4px_rgba(255,255,200,0.2)]" />
        <div className="absolute top-1 right-[30%] w-3 h-3 sm:w-5 sm:h-5 rounded-full bg-white/10 blur-[4px] sm:blur-[6px]" />
        <div className="absolute top-1 right-[30%] w-1.5 h-1.5 sm:w-2.5 sm:h-2.5 rounded-full bg-yellow-100/60 shadow-[0_0_10px_4px_rgba(255,255,200,0.2)]" />
        <div className="absolute top-0 left-0 right-0 h-12 sm:h-20 bg-gradient-to-b from-white/5 to-transparent" />

        <div
          className="absolute inset-0 overflow-hidden"
          style={{
            top: topMargin,
            bottom: bottomMargin,
            left: leftRightMargin,
            right: leftRightMargin,
            borderRadius: '8px',
          }}
        >
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(180deg, #a8d8ea 0%, #87ceeb 20%, #7ec8e3 50%, #87ceeb 80%, #a8d8ea 100%)',
              borderRadius: '8px',
            }}
          />

          <div className="absolute inset-0 overflow-hidden opacity-15">
            {[...Array(14)].map((_, i) => (
              <div
                key={i}
                className="absolute w-full h-[7.14%]"
                style={{
                  top: `${i * 7.14}%`,
                  background: i % 2 === 0 ? 'rgba(255,255,255,0.3)' : 'transparent',
                }}
              />
            ))}
          </div>

          {isMobile ? (
            <>
              <div className="absolute inset-[3%] border-[1.5px] border-white/35 rounded-sm" />
              <div className="absolute left-[3%] right-[3%] top-1/2 h-[1.5px] bg-white/35 -translate-y-1/2" />
              <div className="absolute left-1/2 top-1/2 w-[14%] aspect-square -translate-x-1/2 -translate-y-1/2 rounded-full border-[1.5px] border-white/35" />
              <div className="absolute left-1/2 top-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/50" />
              <div className="absolute left-[20%] right-[20%] top-[3%] h-[16%] border-[1.5px] border-white/35 border-b-0" />
              <div className="absolute left-[33%] right-[33%] top-[3%] h-[7%] border-[1.5px] border-white/35 border-b-0" />
              <div className="absolute left-[20%] right-[20%] bottom-[3%] h-[16%] border-[1.5px] border-white/35 border-t-0" />
              <div className="absolute left-[33%] right-[33%] bottom-[3%] h-[7%] border-[1.5px] border-white/35 border-t-0" />
              <div className="absolute left-1/2 top-[20%] w-1.5 h-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/40" />
              <div className="absolute left-1/2 bottom-[20%] w-1.5 h-1.5 -translate-x-1/2 translate-y-1/2 rounded-full bg-white/40" />
              <div className="absolute left-[3%] top-[3%] w-[4%] aspect-square border-t-[1.5px] border-r-[1.5px] border-white/35 rounded-tr-full" />
              <div className="absolute left-[3%] bottom-[3%] w-[4%] aspect-square border-b-[1.5px] border-r-[1.5px] border-white/35 rounded-br-full" />
              <div className="absolute right-[3%] top-[3%] w-[4%] aspect-square border-t-[1.5px] border-l-[1.5px] border-white/35 rounded-tl-full" />
              <div className="absolute right-[3%] bottom-[3%] w-[4%] aspect-square border-b-[1.5px] border-l-[1.5px] border-white/35 rounded-bl-full" />
            </>
          ) : (
            <>
              <div className="absolute inset-[3%] border-[1.5px] border-white/35 rounded-sm" />
              <div className="absolute left-1/2 top-[3%] bottom-[3%] w-[1.5px] bg-white/35" />
              <div className="absolute left-1/2 top-1/2 w-[14%] aspect-square -translate-x-1/2 -translate-y-1/2 rounded-full border-[1.5px] border-white/35" />
              <div className="absolute left-1/2 top-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/50" />
              <div className="absolute left-[3%] top-1/2 -translate-y-1/2 w-[14%] h-[40%] border-[1.5px] border-white/35 border-l-0 rounded-r-sm" />
              <div className="absolute right-[3%] top-1/2 -translate-y-1/2 w-[14%] h-[40%] border-[1.5px] border-white/35 border-r-0 rounded-l-sm" />
              <div className="absolute left-[3%] top-1/2 -translate-y-1/2 w-[6%] h-[22%] border-[1.5px] border-white/35 border-l-0 rounded-r-sm" />
              <div className="absolute right-[3%] top-1/2 -translate-y-1/2 w-[6%] h-[22%] border-[1.5px] border-white/35 border-r-0 rounded-l-sm" />
              <div className="absolute left-[17%] top-1/2 w-1.5 h-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/40" />
              <div className="absolute right-[17%] top-1/2 w-1.5 h-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/40" />
              <div className="absolute left-[3%] top-[3%] w-[4%] aspect-square border-t-[1.5px] border-r-[1.5px] border-white/35 rounded-tr-full" />
              <div className="absolute left-[3%] bottom-[3%] w-[4%] aspect-square border-b-[1.5px] border-r-[1.5px] border-white/35 rounded-br-full" />
              <div className="absolute right-[3%] top-[3%] w-[4%] aspect-square border-t-[1.5px] border-l-[1.5px] border-white/35 rounded-tl-full" />
              <div className="absolute right-[3%] bottom-[3%] w-[4%] aspect-square border-b-[1.5px] border-l-[1.5px] border-white/35 rounded-bl-full" />
            </>
          )}

          {layout.goalkeeper && (
            <PlayerAvatar
              player={layout.goalkeeper.player}
              x={layout.goalkeeper.x}
              y={layout.goalkeeper.y}
              isGoalkeeper={true}
            />
          )}
          {layout.defenders.map((item, idx) => (
            <PlayerAvatar key={`def-${idx}`} player={item.player} x={item.x} y={item.y} />
          ))}
          {layout.midfielders.map((item, idx) => (
            <PlayerAvatar key={`mid-${idx}`} player={item.player} x={item.x} y={item.y} />
          ))}
          {layout.strikers.map((item, idx) => (
            <PlayerAvatar key={`str-${idx}`} player={item.player} x={item.x} y={item.y} />
          ))}
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-20">
          <div className="bg-gray-900/90 backdrop-blur-md px-3 py-2 sm:px-4 sm:py-3 flex items-center justify-center gap-2 sm:gap-3 border-t border-white/15">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center shadow-lg">
              <FaFutbol className="text-white text-[10px] sm:text-sm" />
            </div>
            <div className="text-center">
              <div className="text-white font-bold text-xs sm:text-sm md:text-base tracking-wide">
                {t(formation.nameKey)} ({formation.short})
              </div>
            </div>
            <div className="hidden sm:block text-gray-400 text-xs">
              {t('formation.hoverForDetails') || 'Hover for details'}
            </div>
          </div>
        </div>

        <div className="absolute top-2 left-2 sm:top-3 sm:left-3 z-20 bg-black/70 backdrop-blur-sm px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg border border-white/15">
          <span className="text-white text-[10px] sm:text-xs font-bold">
            {t('formation.formationView') || 'Formation View'}
          </span>
        </div>
      </div>

      <AnimatePresence>
        {selectedPlayer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-gray-900/95 rounded-2xl max-w-sm w-full p-6 border border-yellow-500/30 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeModal}
                className="absolute top-3 right-3 text-gray-400 hover:text-white transition"
              >
                <FaTimes size={20} />
              </button>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-yellow-400/50">
                  <img
                    src={getPlayerImage(selectedPlayer.position, selectedPlayer.rarity)}
                    alt={selectedPlayer.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.onerror = null; e.target.src = '/img/player.jpg'; }}
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{selectedPlayer.name}</h3>
                  <p className="text-sm text-gray-400">{selectedPlayer.position}</p>
                  {selectedPlayer.is_captain && (
                    <span className="inline-block mt-1 bg-yellow-500/20 text-yellow-400 text-xs px-2 py-0.5 rounded-full">
                      👑 {t('player.captain')}
                    </span>
                  )}
                </div>
                <div className="ml-auto text-right">
                  <div className="text-2xl font-black text-yellow-400">{Math.round(getPlayerPower(selectedPlayer))}</div>
                  <div className="text-[10px] text-gray-500">POWER</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="bg-gray-800/50 rounded-lg p-2 text-center">
                  <div className="text-gray-500 text-[10px]">ATK</div>
                  <div className="font-bold text-blue-400">{selectedPlayer.attack}</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-2 text-center">
                  <div className="text-gray-500 text-[10px]">DEF</div>
                  <div className="font-bold text-green-400">{selectedPlayer.defense}</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-2 text-center">
                  <div className="text-gray-500 text-[10px]">SPD</div>
                  <div className="font-bold text-yellow-400">{selectedPlayer.speed}</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-2 text-center">
                  <div className="text-gray-500 text-[10px]">ACC</div>
                  <div className="font-bold text-purple-400">{selectedPlayer.accuracy}</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-2 text-center">
                  <div className="text-gray-500 text-[10px]">STR</div>
                  <div className="font-bold text-red-400">{selectedPlayer.strength || 50}</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-2 text-center">
                  <div className="text-gray-500 text-[10px]">TAC</div>
                  <div className="font-bold text-cyan-400">{selectedPlayer.tactics || 50}</div>
                </div>
              </div>
              {selectedPlayer.special_ability && (
                <div className="mt-3 text-center text-yellow-300 bg-yellow-500/10 py-1 rounded-lg text-sm">
                  ✨ {selectedPlayer.special_ability}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

// ===== TeamStrengthPanel =====
const TeamStrengthPanel = ({ baseStrength, formationPower, totalStrength, captain, captainBonus }) => {
  const { t } = useTranslation();
  const getLevel = (value) => {
    if (value >= 80) return { label: t('strength.high'), color: 'from-purple-500 to-pink-500', textColor: 'text-purple-300', barColor: 'bg-gradient-to-r from-purple-500 to-pink-500' };
    if (value >= 70) return { label: t('strength.strong'), color: 'from-green-500 to-emerald-500', textColor: 'text-green-300', barColor: 'bg-gradient-to-r from-green-500 to-emerald-500' };
    if (value >= 60) return { label: t('strength.average'), color: 'from-yellow-500 to-orange-500', textColor: 'text-yellow-300', barColor: 'bg-gradient-to-r from-yellow-500 to-orange-500' };
    return { label: t('strength.weak'), color: 'from-red-500 to-rose-500', textColor: 'text-red-300', barColor: 'bg-gradient-to-r from-red-500 to-rose-500' };
  };
  const level = getLevel(totalStrength);
  return (
    <div className="bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-md rounded-2xl p-6 border border-gray-700 shadow-2xl mb-8">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-yellow-500/20"><FaChartLine className="text-yellow-400 text-2xl" /></div>
          <div><h3 className="text-xl font-bold text-white">{t('squadManager.strength')}</h3><p className="text-xs text-gray-400">{t('squadManager.strengthSub')}</p></div>
        </div>
        <div className={`px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs font-bold ${level.textColor}`}>{level.label}</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-5">
        <div className="bg-black/40 rounded-xl p-3 text-center"><div className="text-xs text-gray-400 uppercase">{t('squadManager.basePower')}</div><div className="text-3xl font-black text-blue-400">{Math.round(baseStrength)}</div><div className="text-[10px] text-gray-500">{t('squadManager.basePowerDesc')}</div></div>
        <div className="bg-black/40 rounded-xl p-3 text-center"><div className="text-xs text-gray-400 uppercase">{t('squadManager.formationPower')}</div><div className="text-3xl font-black text-yellow-400">{Math.round(formationPower)}</div><div className="text-[10px] text-gray-500">{t('squadManager.formationPowerDesc')}</div></div>
        <div className={`bg-gradient-to-br ${level.color} rounded-xl p-3 text-center shadow-lg`}><div className="text-xs text-white/80 uppercase">{t('squadManager.totalStrength')}</div><div className="text-4xl font-black text-white drop-shadow-md">{Math.round(totalStrength)}</div><div className="text-[10px] text-white/70">max 100</div></div>
      </div>
      <div className="space-y-2"><div className="flex justify-between text-xs text-gray-400"><span>0</span><span>25</span><span>50</span><span>75</span><span>100</span></div><div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden"><motion.div className={`h-full rounded-full ${level.barColor}`} initial={{ width: 0 }} animate={{ width: `${Math.round(totalStrength)}%` }} transition={{ duration: 0.6 }} /></div></div>
      {captain && (
        <div className="mt-4 flex items-center justify-center gap-2 text-xs bg-yellow-500/10 border border-yellow-500/30 rounded-full py-1.5 px-4 w-fit mx-auto">
          <FaCrown className="text-yellow-400" />
          <span className="text-yellow-300">{t('squadManager.captain')}: {captain.name} {captain.special_ability ? `(${captain.special_ability})` : ''}</span>
          <span className="text-gray-400">+{Math.round(captainBonus)} {t('squadManager.strength')}</span>
        </div>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------
const MainGame = ({ userId, onLogout }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const { socket, consumePendingMatch, consumePendingFinishedMatch } = useSocket();
  const [team, setTeam] = useState([]);
  const [dailyMatches, setDailyMatches] = useState({ domestic: 5, champions: 5, europa: 5, friendly: 3 });
  // ✅ تغيير seasonMatches إلى كائن يحتوي على ثلاث مسابقات
  const [seasonMatches, setSeasonMatches] = useState({ domestic: 0, champions: 0, europa: 0 });
  const [loading, setLoading] = useState(true);
  const [showMatch, setShowMatch] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [matchVersion, setMatchVersion] = useState(0);
  const [searching, setSearching] = useState(false);
  const [waitingMsg, setWaitingMsg] = useState('');
  const [searchTimer, setSearchTimer] = useState(0);
  const [showTimer, setShowTimer] = useState(false);
  const searchIntervalRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const [showStandings, setShowStandings] = useState(false);
  const [showHallOfFame, setShowHallOfFame] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [selectedFormation, setSelectedFormation] = useState(formations[0]);
  const [playingStyle, setPlayingStyle] = useState('balanced');
  const [teamAverage, setTeamAverage] = useState({ attack: 0, defense: 0, speed: 0, accuracy: 0, strength: 0, tactics: 0 });
  const [showFormationInfo, setShowFormationInfo] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [userContinent, setUserContinent] = useState('Europe');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState('domestic');
  const [isQualifiedForChampions, setIsQualifiedForChampions] = useState(false);
  const [isQualifiedForEuropa, setIsQualifiedForEuropa] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showTacticalView, setShowTacticalView] = useState(false);
  const [tacticalActive, setTacticalActive] = useState([]);
  const [tacticalBench, setTacticalBench] = useState([]);
  const [tacticalLoading, setTacticalLoading] = useState(false);
  const [finishedMatch, setFinishedMatch] = useState(null);
  const [toast, setToast] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const lastProcessedMatchIdRef = useRef(null);
  const isProcessingRef = useRef(false);
  const processingTimeoutRef = useRef(null);
  const resumeHandlerTimeoutRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const desktopNotifRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  // ✅ CHANGED: استخدم useRef بدلاً من useState لـ rejoinAttempted لتجنب مشاكل StrictMode
  const rejoinSentRef = useRef(false);
  const [seasonEnded, setSeasonEnded] = useState(false);
  const [seasonEndMessage, setSeasonEndMessage] = useState('');
  const seasonEndedShownRef = useRef(false);

  // ✅ NEW: استخدام ref للتحكم في تحديث الإعدادات (لتجنب الحلقات اللانهائية)
  const settingsUpdateTimeoutRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const loadNotifications = useCallback(async () => {
    setNotifLoading(true);
    try {
      const res = await fetchNotifications();
      const validNotifs = (res.data || []).filter(n => n && n.id);
      setNotifications(validNotifs);
    } catch (err) {
      // ✅ NEW: تسجيل الخطأ
      await reportError(err, {
        userId,
        component: 'MainGame.loadNotifications',
      });
    } finally {
      setNotifLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    if (showNotifications) loadNotifications();
  }, [showNotifications, loadNotifications]);

  useEffect(() => {
    if (isMobile) return;
    const handleClickOutside = (event) => {
      if (desktopNotifRef.current && !desktopNotifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile]);

  useEffect(() => {
    const handleOnline = () => {
      //console.log('🌐 Internet connection restored');
      setIsOnline(true);
      setToast({ message: 'Internet restored, reconnecting...', type: 'success' });
      setTimeout(() => {
        if (socket && !socket.connected) {
          socket.connect();
          setTimeout(() => socket.emit('rejoin-match'), 1000);
        }
      }, 500);
      setTimeout(() => setToast(null), 3000);
    };
    const handleOffline = () => {
      //console.log('🌐 Internet connection lost');
      setIsOnline(false);
      setToast({ message: 'No internet connection', type: 'warning' });
      setTimeout(() => setToast(null), 3000);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket) return;
    const pingInterval = setInterval(() => {
      if (socket && socket.connected) socket.emit('ping');
      else if (socket && !socket.connected && navigator.onLine) socket.connect();
    }, 15000);
    socket.on('pong', () => {});
    return () => {
      clearInterval(pingInterval);
      socket.off('pong');
    };
  }, [socket]);

  const handleMarkRead = async (id) => {
    if (!id) return;
    try {
      await markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch (err) {
      // ✅ NEW: تسجيل الخطأ
      await reportError(err, {
        userId,
        component: 'MainGame.handleMarkRead',
        notificationId: id,
      });
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    try {
      await deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      // ✅ NEW: تسجيل الخطأ
      await reportError(err, {
        userId,
        component: 'MainGame.handleDelete',
        notificationId: id,
      });
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      // ✅ NEW: تسجيل الخطأ
      await reportError(err, {
        userId,
        component: 'MainGame.handleMarkAllRead',
      });
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  useEffect(() => {
    const name = localStorage.getItem('username');
    if (name) setPlayerName(name);
    const continent = localStorage.getItem('continent');
    if (continent) setUserContinent(continent);
  }, []);

  const captain = team.find(p => p && p.is_captain === true);
  const baseStrengthRaw = team.length ? calculateBaseTeamStrength(team) : 0;
  const formationPowerRaw = team.length ? calculateFormationPower(team, selectedFormation.id, playingStyle) : 0;
  const totalStrengthRaw = team.length ? calculateFinalTeamStrength(baseStrengthRaw, formationPowerRaw, captain) : 0;
  const strengthWithoutCaptainRaw = team.length ? calculateFinalTeamStrength(baseStrengthRaw, formationPowerRaw, null) : 0;
  let captainBonusRaw = 0;
  if (captain && team.length) {
    const combined = (baseStrengthRaw + formationPowerRaw) / 2;
    captainBonusRaw = combined * 0.05;
    if (captain.special_ability === 'Playmaker') captainBonusRaw += combined * 0.05;
    else if (captain.special_ability === 'Super Scorer' && captain.position === 'Striker') captainBonusRaw += combined * 0.10;
    else if (captain.special_ability === 'Super Saver' && captain.position === 'Goalkeeper') captainBonusRaw += combined * 0.10;
    else if (captain.special_ability === 'Speed Runner') captainBonusRaw += combined * 0.07;
  }

  // ✅ NEW: تحميل البيانات الأولية مع قراءة الإعدادات المحفوظة
  const loadInitialData = useCallback(async () => {
    try {
      const [teamRes, profileRes, dailyStats] = await Promise.all([fetchUserTeam(userId), getProfile(userId), getUserDailyStats(userId)]);
      const userTeam = (teamRes.data || []).filter(p => p && typeof p === 'object');
      setTeam(userTeam);
      setTeamAverage(calculateTeamAverage(userTeam));
      setDailyMatches({
        domestic: dailyStats.domestic ?? 5,
        champions: dailyStats.champions ?? 5,
        europa: dailyStats.europa ?? 5,
        friendly: dailyStats.friendly ?? 3
      });
      // ✅ قراءة المباريات الموسمية لكل مسابقة من الـ profile
      const domesticSeason = profileRes.data.domestic_matches_this_season ?? 0;
      const championsSeason = profileRes.data.champions_matches_this_season ?? 0;
      const europaSeason = profileRes.data.europa_matches_this_season ?? 0;
      setSeasonMatches({
        domestic: domesticSeason,
        champions: championsSeason,
        europa: europaSeason
      });
      if (profileRes.data.continent) setUserContinent(profileRes.data.continent);

      // ✅ NEW: قراءة التشكيلة وأسلوب اللعب المحفوظة
      if (profileRes.data.formation) {
        const savedFormation = formations.find(f => f.id === profileRes.data.formation);
        if (savedFormation) {
          setSelectedFormation(savedFormation);
        }
      }
      if (profileRes.data.playing_style) {
        setPlayingStyle(profileRes.data.playing_style);
      }

      try {
        const [champsQualified, europaQualified] = await Promise.all([
          fetchQualifiedUsers('champions'),
          fetchQualifiedUsers('europa')
        ]);
        setIsQualifiedForChampions(champsQualified.data.some(row => row.user_id === userId));
        setIsQualifiedForEuropa(europaQualified.data.some(row => row.user_id === userId));
      } catch (err) {
        // ✅ NEW: تسجيل الخطأ
        await reportError(err, {
          userId,
          component: 'MainGame.loadInitialData.qualification',
        });
        try {
          const champsRes = await fetchStandings('champions');
          const europaRes = await fetchStandings('europa');
          setIsQualifiedForChampions(champsRes.data.some(row => row.user_id === userId));
          setIsQualifiedForEuropa(europaRes.data.some(row => row.user_id === userId));
        } catch (e) {
          // ✅ NEW: تسجيل الخطأ الداخلي
          await reportError(e, {
            userId,
            component: 'MainGame.loadInitialData.fallbackStandings',
          });
        }
      }
      if (userContinent) {
        try {
          const seasonRes = await fetchCurrentSeason('domestic', userContinent);
          const currentSeasonNum = seasonRes.data.season_number;
          const storageKey = `last_season_${userContinent}_${userId}`;
          const lastSeasonNum = localStorage.getItem(storageKey);
          if (lastSeasonNum && parseInt(lastSeasonNum) !== currentSeasonNum) {
            setToast({
              message: `🎉 New domestic season #${currentSeasonNum} has started! Standings have been reset. Good luck!`,
              type: 'success'
            });
            setTimeout(() => setToast(null), 5000);
          }
          localStorage.setItem(storageKey, currentSeasonNum);
        } catch (err) {
          // ✅ NEW: تسجيل الخطأ
          await reportError(err, {
            userId,
            component: 'MainGame.loadInitialData.seasonCheck',
          });
          console.error('Failed to check season change:', err);
        }
      }
    } catch (err) {
      // ✅ NEW: تسجيل الخطأ الرئيسي
      await reportError(err, {
        userId,
        component: 'MainGame.loadInitialData',
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [userId, userContinent]);

const saveSettings = useCallback(async (formationId, styleId) => {
  if (settingsUpdateTimeoutRef.current) {
    clearTimeout(settingsUpdateTimeoutRef.current);
  }
  settingsUpdateTimeoutRef.current = setTimeout(async () => {
    try {
      // ✅ تأكد من أن القيم صالحة قبل الإرسال
      const validFormations = ['counter', 'aggressive', 'traditionalAttack', 'traditionalDefense', 'highPress', 'advancedDefense'];
      const validStyles = ['balanced', 'long_ball', 'short_pass'];
      
      if (!validFormations.includes(formationId) || !validStyles.includes(styleId)) {
        console.warn('Invalid formation or playing style, skipping save:', { formationId, styleId });
        return;
      }
      
      await updateUserSettings(formationId, styleId);
      //console.log('✅ Settings saved:', { formation: formationId, playingStyle: styleId });
    } catch (err) {
      console.error('Failed to save settings:', err);
      // ✅ تجاهل الخطأ إذا كان 400 (قد يكون بسبب validation لكن التحديث تم)
      if (err.response?.status === 400) {
        console.warn('⚠️ Validation warning (settings may still be saved):', err.response?.data);
      }
    }
  }, 500);
}, []);

  // ✅ NEW: دالة معالجة تغيير التشكيلة مع الحفظ
  const handleFormationChange = useCallback((formation) => {
    setSelectedFormation(formation);
    saveSettings(formation.id, playingStyle);
  }, [playingStyle, saveSettings]);

  // ✅ NEW: دالة معالجة تغيير أسلوب اللعب مع الحفظ
  const handlePlayingStyleChange = useCallback((styleId) => {
    setPlayingStyle(styleId);
    saveSettings(selectedFormation.id, styleId);
  }, [selectedFormation, saveSettings]);

  const refreshDailyStats = async () => {
    try {
      const stats = await getUserDailyStats(userId);
      setDailyMatches({
        domestic: stats.domestic ?? 5,
        champions: stats.champions ?? 5,
        europa: stats.europa ?? 5,
        friendly: stats.friendly ?? 3
      });
      const profileRes = await getProfile(userId);
      if (profileRes.data) {
        const domesticSeason = profileRes.data.domestic_matches_this_season ?? 0;
        const championsSeason = profileRes.data.champions_matches_this_season ?? 0;
        const europaSeason = profileRes.data.europa_matches_this_season ?? 0;
        setSeasonMatches({
          domestic: domesticSeason,
          champions: championsSeason,
          europa: europaSeason
        });
      }
    } catch (err) {
      // ✅ NEW: تسجيل الخطأ
      await reportError(err, {
        userId,
        component: 'MainGame.refreshDailyStats',
      });
      console.error(err);
    }
  };

  useEffect(() => { loadInitialData(); }, [loadInitialData]);

  useEffect(() => {
    const checkSeasons = async () => {
      if (!userContinent) return;
      try {
        const competitions = [
          { comp: 'domestic', continent: userContinent },
          { comp: 'champions', continent: null },
          { comp: 'europa', continent: null }
        ];
        let ended = false;
        let endedNames = [];
        for (const { comp, continent } of competitions) {
          const res = await fetchCurrentSeason(comp, continent);
          const data = res.data;
          if (data && data.end_date && new Date(data.end_date) < new Date()) {
            ended = true;
            endedNames.push(t(`competition.${comp}`));
          }
        }
        if (ended) {
          setSeasonEnded(true);
          setSeasonEndMessage(t('season.endedMessage', { competitions: endedNames.join(', ') }));
        } else {
          setSeasonEnded(false);
          seasonEndedShownRef.current = false;
        }
      } catch (err) {
        // ✅ NEW: تسجيل الخطأ
        await reportError(err, {
          userId,
          component: 'MainGame.checkSeasons',
        });
        console.error('Failed to check seasons:', err);
      }
    };
    checkSeasons();
  }, [userContinent, t]);

  useEffect(() => {
    if (seasonEnded && !seasonEndedShownRef.current) {
      seasonEndedShownRef.current = true;
      setToast({
        message: seasonEndMessage,
        type: 'warning'
      });
      setTimeout(() => setToast(null), 6000);
    }
  }, [seasonEnded, seasonEndMessage]);

  useEffect(() => {
    const pendingMatch = consumePendingMatch();
    if (pendingMatch && !isProcessingRef.current) {
      if (showMatch && matchData?.matchId === pendingMatch.matchId) {
        //console.log('⏩ [MainGame] Already showing this match, ignoring');
        return;
      }
      if (lastProcessedMatchIdRef.current === pendingMatch.matchId) {
        //console.log('⏩ [MainGame] Ignoring duplicate match', pendingMatch.matchId);
        return;
      }
      isProcessingRef.current = true;
      lastProcessedMatchIdRef.current = pendingMatch.matchId;
      if (processingTimeoutRef.current) clearTimeout(processingTimeoutRef.current);
      //console.log('🎮 [MainGame] Processing match', pendingMatch.matchId);
      setFinishedMatch(null);
      setTimeout(() => {
        setMatchData(pendingMatch);
        setShowMatch(true);
        if (matchData?.matchId !== pendingMatch.matchId) {
          setMatchVersion(prev => prev + 1);
        }
      }, 100);
      processingTimeoutRef.current = setTimeout(() => {
        isProcessingRef.current = false;
        processingTimeoutRef.current = null;
      }, 3000);
    }
  }, [consumePendingMatch, matchData?.matchId, showMatch]);

  useEffect(() => {
    const finished = consumePendingFinishedMatch();
    if (finished && finished.result) {
      //console.log('🎮 [MainGame] Displaying finished match from pending', finished);
      if (!searching && !showMatch) {
        setFinishedMatch(finished.result);
      }
    }
  }, [consumePendingFinishedMatch, searching, showMatch]);

  const clearSearchTimers = () => {
    if (searchIntervalRef.current) clearInterval(searchIntervalRef.current);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  };

  const cancelSearch = useCallback(() => {
    if (!searching) return;
    clearSearchTimers();
    if (socket) socket.emit('cancel-search', userId);
    setSearching(false);
    setShowTimer(false);
    setSearchTimer(0);
    setWaitingMsg('');
  }, [searching, socket, userId]);

  useEffect(() => {
    if (!socket) return;
    const handleMatchStart = (data) => {
      clearSearchTimers();
      setSearching(false);
      setShowTimer(false);
      setFinishedMatch(null);
      setMatchData(data);
      setShowMatch(true);
      setMatchVersion(prev => prev + 1);
    };
    const handleMatchError = (err) => { 
      clearSearchTimers(); 
      setSearching(false); 
      setShowTimer(false); 
      setToast({ message: err.error, type: 'error' });
      setTimeout(() => setToast(null), 5000);
      setWaitingMsg(''); 
    };
    const handleResumeMatch = (data) => {
      if (showMatch && matchData?.matchId === data.matchId) {
        //console.log('⏩ [MainGame] Already showing this match, ignoring resume');
        return;
      }
      if (isProcessingRef.current) {
        //console.log('⏩ [MainGame] Already processing, skipping resume-match');
        return;
      }
      if (lastProcessedMatchIdRef.current === data.matchId) {
        //console.log('⏩ [MainGame] Match already processed, skipping resume-match', data.matchId);
        return;
      }
      if (resumeHandlerTimeoutRef.current) clearTimeout(resumeHandlerTimeoutRef.current);
      isProcessingRef.current = true;
      lastProcessedMatchIdRef.current = data.matchId;
      //console.log('🎮 [MainGame] Processing resume-match:', data.matchId);
      clearSearchTimers();
      setSearching(false);
      setShowTimer(false);
      setFinishedMatch(null);
      setMatchData(data);
      setShowMatch(true);
      if (matchData?.matchId !== data.matchId) {
        setMatchVersion(prev => prev + 1);
      }
      resumeHandlerTimeoutRef.current = setTimeout(() => {
        isProcessingRef.current = false;
        resumeHandlerTimeoutRef.current = null;
      }, 3000);
    };
    const handleSearchCancelled = () => { 
      clearSearchTimers(); 
      setSearching(false); 
      setShowTimer(false); 
      setWaitingMsg(''); 
    };
    const handleMatchFinished = (data) => {
      //console.log('🏁 [MainGame] Direct match-finished received', data);
      if (data && data.result && !showMatch && !searching) {
        setFinishedMatch(data.result);
      }
    };
    const handleDisconnect = (reason) => {
      //console.log('🔌 Socket disconnected:', reason);
      setToast({ 
        message: t('common.connectionLost') || 'Connection lost. Reconnecting...', 
        type: 'warning' 
      });
    };
    const handleReconnecting = (attemptNumber) => {
      //console.log('🔄 Reconnecting attempt:', attemptNumber);
      setToast({ 
        message: `Reconnecting... (attempt ${attemptNumber})`, 
        type: 'info' 
      });
    };
    const handleReconnect = (attemptNumber) => {
      //console.log('✅ Reconnected after', attemptNumber, 'attempts');
      setToast({ 
        message: 'Connection restored!', 
        type: 'success' 
      });
      setTimeout(() => setToast(null), 3000);
      socket.emit('rejoin-match');
    };
    const handleReconnectFailed = () => {
      //console.error('❌ Failed to reconnect');
      setToast({ 
        message: 'Failed to reconnect. Please refresh the page.', 
        type: 'error' 
      });
    };

    socket.on('match-start', handleMatchStart);
    socket.on('match-error', handleMatchError);
    socket.on('resume-match', handleResumeMatch);
    socket.on('search-cancelled', handleSearchCancelled);
    socket.on('match-finished', handleMatchFinished);
    socket.on('disconnect', handleDisconnect);
    socket.on('reconnecting', handleReconnecting);
    socket.on('reconnect', handleReconnect);
    socket.on('reconnect_failed', handleReconnectFailed);

    // ✅ CHANGED: إرسال rejoin-match عند الاتصال الأولي (مرة واحدة فقط)
    if (socket.connected && !rejoinSentRef.current) {
      rejoinSentRef.current = true;
      //console.log('🔄 [MainGame] Sending rejoin-match on initial connection');
      socket.emit('rejoin-match');
    }

    return () => {
      socket.off('match-start', handleMatchStart);
      socket.off('match-error', handleMatchError);
      socket.off('resume-match', handleResumeMatch);
      socket.off('search-cancelled', handleSearchCancelled);
      socket.off('match-finished', handleMatchFinished);
      socket.off('disconnect', handleDisconnect);
      socket.off('reconnecting', handleReconnecting);
      socket.off('reconnect', handleReconnect);
      socket.off('reconnect_failed', handleReconnectFailed);
      if (processingTimeoutRef.current) clearTimeout(processingTimeoutRef.current);
      if (resumeHandlerTimeoutRef.current) clearTimeout(resumeHandlerTimeoutRef.current);
    };
  }, [socket, userId, t]);

  const openTacticalView = async () => {
    setTacticalLoading(true);
    try {
      const res = await fetchUserSquad(userId);
      const allCards = res.data || [];
      const active = allCards.filter(c => c.is_active).sort((a, b) => a.position_index - b.position_index);
      const bench = allCards.filter(c => !c.is_active).sort((a, b) => a.position_index - b.position_index);
      setTacticalActive(active);
      setTacticalBench(bench);
      setShowTacticalView(true);
    } catch (err) {
      // ✅ NEW: تسجيل الخطأ
      await reportError(err, {
        userId,
        component: 'MainGame.openTacticalView',
      });
      console.error('Failed to load squad for tactical view:', err);
      setToast({ message: t('squadManager.loadError'), type: 'error' });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setTacticalLoading(false);
    }
  };

  const handleTacticalSwap = async (activeIdx, benchIdx) => {
    if (activeIdx === null || benchIdx === null) return;
    const newActive = [...tacticalActive];
    const newBench = [...tacticalBench];
    const temp = newActive[activeIdx];
    newActive[activeIdx] = newBench[benchIdx];
    newBench[benchIdx] = temp;
    setTacticalActive(newActive);
    setTacticalBench(newBench);
    try {
      await saveUserSquad(userId, newActive.map(c => c.id), newBench.map(c => c.id));
      await loadInitialData();
      setToast({ message: t('squadManager.swapCompleted'), type: 'success' });
      setTimeout(() => setToast(null), 2000);
      await openTacticalView();
    } catch (err) {
      // ✅ NEW: تسجيل الخطأ
      await reportError(err, {
        userId,
        component: 'MainGame.handleTacticalSwap.save',
        activeIdx,
        benchIdx,
      });
      console.error('Failed to save squad after swap:', err);
      setToast({ message: t('squadManager.saveError'), type: 'error' });
      setTimeout(() => setToast(null), 4000);
      await openTacticalView();
    }
  };

  const handleTacticalSetCaptain = async (cardId) => {
    try {
      await setCaptainApi(cardId);
      await loadInitialData();
      await openTacticalView();
      setToast({ message: t('squadManager.captainUpdated'), type: 'success' });
      setTimeout(() => setToast(null), 2000);
    } catch (err) {
      // ✅ NEW: تسجيل الخطأ
      await reportError(err, {
        userId,
        component: 'MainGame.handleTacticalSetCaptain',
        cardId,
      });
      console.error('Failed to set captain:', err);
      setToast({ message: t('squadManager.captainError'), type: 'error' });
      setTimeout(() => setToast(null), 4000);
    }
  };

  const handleTacticalSave = async () => {
    try {
      await saveUserSquad(
        userId,
        tacticalActive.map(p => p.id),
        tacticalBench.map(p => p.id)
      );
      setToast({ message: t('squadManager.squadSaved'), type: 'success' });
      setTimeout(() => setToast(null), 3000);
      await loadInitialData();
      await openTacticalView();
    } catch (err) {
      // ✅ NEW: تسجيل الخطأ
      await reportError(err, {
        userId,
        component: 'MainGame.handleTacticalSave',
        activeIds: tacticalActive.map(p => p.id),
        benchIds: tacticalBench.map(p => p.id),
      });
      console.error('Failed to save squad:', err);
      setToast({ message: t('squadManager.saveError'), type: 'error' });
      setTimeout(() => setToast(null), 4000);
    }
  };

  // ✅ NEW: تعديل findMatch لاستخدام الإعدادات المحفوظة
  const findMatch = useCallback(() => {
    if (seasonEnded) {
      setToast({
        message: t('season.endedCannotPlay'),
        type: 'warning'
      });
      setTimeout(() => setToast(null), 4000);
      return;
    }
    if (finishedMatch) setFinishedMatch(null);
    if (!socket) {
      console.error('❌ [MainGame] Socket not available!');
      setToast({ message: t('common.connectionLost') || 'Connection lost', type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    const validCompetitions = ['domestic', 'champions', 'europa', 'friendly'];
    if (!selectedCompetition || !validCompetitions.includes(selectedCompetition)) {
      console.error('❌ [MainGame] Invalid competition:', selectedCompetition);
      setToast({ message: 'Invalid competition selected. Please try again.', type: 'error' });
      setTimeout(() => setToast(null), 3000);
      setSelectedCompetition('domestic');
      return;
    }

    const seasonLimit = 100;
    if (selectedCompetition !== 'friendly') {
      const currentSeasonMatches = seasonMatches[selectedCompetition] ?? 0;
      if (currentSeasonMatches >= seasonLimit) {
        setToast({
          message: t('season.seasonLimitReached'),
          type: 'warning'
        });
        setTimeout(() => setToast(null), 4000);
        return;
      }
    }

    if (selectedCompetition === 'friendly') {
      const totalMatches = Object.values(seasonMatches).reduce((a, b) => a + b, 0);
      if (totalMatches < 100) {
        setToast({
          message: t('competition.friendlyLocked', { count: 100 - totalMatches }),
          type: 'warning'
        });
        setTimeout(() => setToast(null), 4000);
        return;
      }
    }

    const dailyLimit = selectedCompetition === 'friendly' ? 3 : 5;
    const remaining = dailyMatches[selectedCompetition];
    if (remaining <= 0) {
      setToast({
        message: t('competition.dailyLimit'),
        type: 'warning'
      });
      setTimeout(() => setToast(null), 4000);
      return;
    }
    if (searching) return;
    if (selectedCompetition === 'domestic' && !userContinent) {
      setToast({
        message: t('competition.missingContinent'),
        type: 'error'
      });
      setTimeout(() => setToast(null), 4000);
      return;
    }
    if (selectedCompetition === 'champions' && !isQualifiedForChampions) {
      setToast({
        message: t('competition.notQualifiedChampions'),
        type: 'warning'
      });
      setTimeout(() => setToast(null), 4000);
      return;
    }
    if (selectedCompetition === 'europa' && !isQualifiedForEuropa) {
      setToast({
        message: t('competition.notQualifiedEuropa'),
        type: 'warning'
      });
      setTimeout(() => setToast(null), 4000);
      return;
    }
    setSearching(true);
    setWaitingMsg(`${t('match.finding')} (${t('squadManager.strength')}: ${Math.round(totalStrengthRaw)})...`);
    setShowTimer(true);
    setSearchTimer(60);
    const searchParams = {
      userId,
      formation: selectedFormation.id,
      competition: selectedCompetition,
      continent: selectedCompetition === 'domestic' ? userContinent : null,
      playingStyle,
    };
    //console.log('📤 [MainGame] Emitting find-match event with params:', searchParams);
    socket.emit('find-match', searchParams);
    searchIntervalRef.current = setInterval(() => {
      setSearchTimer((prev) => {
        if (prev <= 1) {
          clearInterval(searchIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    searchTimeoutRef.current = setTimeout(() => {
      if (searching) {
        cancelSearch();
        setToast({
          message: t('match.noOpponent'),
          type: 'warning'
        });
        setTimeout(() => setToast(null), 4000);
      }
    }, 60000);
  }, [seasonEnded, finishedMatch, socket, selectedCompetition, seasonMatches, dailyMatches, searching, userContinent, isQualifiedForChampions, isQualifiedForEuropa, totalStrengthRaw, selectedFormation, playingStyle, cancelSearch, t]);

  const onMatchFinish = () => {
    setShowMatch(false);
    setMatchData(null);
    setFinishedMatch(null);
    cancelSearch();
    refreshDailyStats();
    loadInitialData();
    isProcessingRef.current = false;
    lastProcessedMatchIdRef.current = null;
  };

  const openTutorial = () => setShowTutorial(true);

  const ToastMessage = () => {
    if (!toast) return null;
    const bgColor = toast.type === 'error' ? 'bg-red-600' : toast.type === 'success' ? 'bg-green-600' : toast.type === 'warning' ? 'bg-yellow-600' : 'bg-blue-600';
    const icon = toast.type === 'error' ? <FaExclamationTriangle /> : toast.type === 'success' ? <FaCheckCircle /> : <FaWifi />;
    return (
      <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[60] animate-in fade-in slide-in-from-top-5 duration-300">
        <div className={`${bgColor} text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-3 backdrop-blur-sm border border-white/20`}>
          {icon}
          <span className="text-sm font-medium">{toast.message}</span>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (userId) {
      const timer = setTimeout(() => {
        requestNotificationPermission(userId);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [userId]);

  useEffect(() => {
    const unsubscribe = listenForNotifications((payload) => {
      //console.log('📢 Foreground notification received:', payload);
      setToast({
        message: `${payload.title}: ${payload.body}`,
        type: 'success'
      });
      setTimeout(() => setToast(null), 5000);
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // ============================================================
  // ✅ NEW: healthCheck كل 10 ثوانٍ (بدلاً من 5)
  // ============================================================
  useEffect(() => {
    if (!socket) return;
    const healthCheck = setInterval(() => {
      if (socket && socket.connected) {
        socket.emit('get-match-state');
      } else if (socket && !socket.connected && navigator.onLine) {
        //console.log('🔄 [MainGame] healthCheck: reconnecting...');
        socket.connect();
        setTimeout(() => socket.emit('rejoin-match'), 500);
      }
    }, 10000); // ✅ CHANGED: من 5000 إلى 10000
    return () => clearInterval(healthCheck);
  }, [socket]);

  if (finishedMatch) {
    const getCardImage = (card) => {
      const positionMap = { Striker: 'striker', Defender: 'defender', Midfielder: 'midfielder', Goalkeeper: 'goalkeeper' };
      const baseName = positionMap[card.position] || 'player';
      if (card.rarity === 'gold' || card.rarity === 'silver' || card.rarity === 'bronze') {
        return `/img/${card.rarity}_${baseName}.jpg`;
      }
      return `/img/${baseName}.jpg`;
    };
    const StatWithArrow = ({ value, diff, label }) => {
      let arrow = null;
      if (diff > 0) arrow = <span className="text-green-500 text-xs ml-1 animate-bounce">▲</span>;
      else if (diff < 0) arrow = <span className="text-red-500 text-xs ml-1 animate-bounce">▼</span>;
      else arrow = <span className="text-gray-500 text-xs ml-1">•</span>;
      return (
        <div className="flex items-center justify-between bg-black/40 rounded-lg p-2">
          <span className="text-gray-400 text-xs">{label}</span>
          <div className="flex items-center">
            <span className="font-mono text-sm font-bold text-white">{value}</span>
            {arrow}
          </div>
        </div>
      );
    };
    return (
      <div className="min-h-screen bg-black/90 text-white p-6 flex flex-col items-center justify-center font-cairo" dir={i18n.dir()}>
        <ToastMessage />
        <motion.h1 initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="text-5xl font-bold mb-4 text-yellow-400">{t('match.matchFinished')}</motion.h1>
        <p className="text-3xl font-bold mb-2">{finishedMatch.yourScore} – {finishedMatch.opponentScore}</p>
        <p className="text-lg mt-2">{finishedMatch.winner === 'you' ? t('match.youWon') : finishedMatch.winner === 'opponent' ? t('match.youLost') : t('match.draw')}</p>
        <h3 className="text-xl mt-6 mb-3 flex items-center gap-2">{t('match.cardEvolution')}<span className="text-sm text-green-400 animate-pulse">{t('match.statsUpdated')}</span></h3>
        <div className="max-h-96 overflow-y-auto w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-4">
          {(finishedMatch.cards || []).map((card, idx) => {
            const hasChanges = card.diff && (card.diff.attack !== 0 || card.diff.defense !== 0 || card.diff.speed !== 0 || card.diff.accuracy !== 0 || card.diff.strength !== 0 || card.diff.tactics !== 0);
            return (
              <motion.div key={idx} initial={{ opacity: 0, y: 30, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: idx * 0.1, type: 'spring', stiffness: 200 }} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 border border-gray-700 hover:border-yellow-500/50 transition-all shadow-xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-700 ring-2 ring-yellow-500/50">
                      <img src={getCardImage(card)} alt={card.name} className="w-full h-full object-cover" onError={e => { e.target.onerror = null; e.target.src = '/img/player.jpg'; }} />
                    </div>
                    <div><div className="font-bold text-white text-lg">{card.name}</div><div className="text-xs text-gray-400">{card.position || 'Player'}</div></div>
                  </div>
                  {hasChanges && <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: idx * 0.1 + 0.3, type: 'spring' }} className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full flex items-center gap-1"><span>✨</span>{t('match.updated')}</motion.div>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <StatWithArrow value={card.attack} diff={card.diff?.attack} label={t('statistics.attack')} />
                  <StatWithArrow value={card.defense} diff={card.diff?.defense} label={t('statistics.defense')} />
                  <StatWithArrow value={card.speed} diff={card.diff?.speed} label={t('statistics.speed')} />
                  <StatWithArrow value={card.accuracy} diff={card.diff?.accuracy} label={t('statistics.accuracy')} />
                  <StatWithArrow value={card.strength || 50} diff={card.diff?.strength} label="💪 STR" />
                  <StatWithArrow value={card.tactics || 50} diff={card.diff?.tactics} label="🧠 TAC" />
                </div>
                {!hasChanges && <div className="text-center text-gray-500 text-xs mt-2">{t('match.noChanges')}</div>}
              </motion.div>
            );
          })}
        </div>
        <button onClick={onMatchFinish} className="mt-8 bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-3 rounded-full font-bold transition transform hover:scale-105 shadow-lg">{t('match.backToGame')}</button>
      </div>
    );
  }

  if (showHallOfFame) return <HallOfFame onBack={() => setShowHallOfFame(false)} />;
  if (loading) return <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black flex items-center justify-center font-cairo"><div className="text-center"><div className="animate-spin rounded-full h-16 w-16 border-t-4 border-yellow-400 border-solid mb-4"></div><p className="text-white text-xl font-mono">{t('common.loading')}</p></div></div>;
  if (showProfile) return <ProfilePage userId={userId} onBack={() => setShowProfile(false)} />;
  if (showStatistics) return <StatisticsPage userId={userId} onBack={() => setShowStatistics(false)} />;
  if (showStandings) return <StandingsPage onBack={() => setShowStandings(false)} userContinent={userContinent} />;

 if (showMatch && matchData) {
  const competition = matchData.competition || selectedCompetition;
  
  const tournamentTypeMap = {
    'domestic': 'domestic_league',
    'champions': 'champions_league',
    'europa': 'professional_league',
    'friendly': 'domestic_league'
  };
  
  const tournamentNameMap = {
    'domestic': `${userContinent} Domestic League`,
    'champions': 'UEFA Champions League',
    'europa': 'Professional League',
    'friendly': 'Friendly Match'
  };
  
  const enhancedMatchData = {
    ...matchData,
    tournamentType: matchData.tournamentType || tournamentTypeMap[competition] || 'domestic_league',
    tournamentName: matchData.tournamentName || tournamentNameMap[competition] || 'Match'
  };
  
  //console.log('🎮 [MainGame] Starting match with tournament:', enhancedMatchData.tournamentType, 'competition:', competition);
  
  return (
    <LiveMatch 
      key={`${matchData.matchId}-${matchVersion}`} 
      matchData={enhancedMatchData} 
      userId={userId} 
      onFinish={onMatchFinish} 
    />
  );
}

  if (showTacticalView) {
    return (
      <>
        <ToastMessage />
        <SquadTacticalView
          activeSquad={tacticalActive}
          benchSquad={tacticalBench}
          onSwap={handleTacticalSwap}
          onBack={() => {
            setShowTacticalView(false);
            loadInitialData();
          }}
          onSave={handleTacticalSave}
          onSetCaptain={handleTacticalSetCaptain}
          teamStrength={Math.round(totalStrengthRaw)}
          strengthWithoutCaptain={Math.round(strengthWithoutCaptainRaw)}
          captainBonus={Math.round(captainBonusRaw)}
          captain={captain}
          t={t}
          toast={toast}
          setToast={setToast}
        />
      </>
    );
  }

  const NotificationsModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-gray-800 rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col shadow-2xl border border-yellow-500/30"
      >
        <div className="flex justify-between items-center p-4 border-b border-white/10 sticky top-0 bg-gray-800 rounded-t-2xl">
          <h3 className="font-bold text-white text-lg">{t('notifications.title')}</h3>
          <div className="flex items-center gap-3">
            {unreadCount > 0 && (
              <button onClick={handleMarkAllRead} className="text-xs text-yellow-400 hover:underline">
                {t('notifications.markAllRead')}
              </button>
            )}
            <button onClick={() => setShowNotifications(false)} className="text-gray-400 hover:text-white">
              <FaTimes size={20} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {notifLoading ? (
            <div className="p-4 text-center text-gray-400 text-sm">{t('common.loading')}</div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-gray-400 text-sm">{t('notifications.noNotifications')}</div>
          ) : (
            notifications.filter(n => n && n.id).map(notif => (
              <div key={notif.id} className={`p-3 border-b border-white/5 hover:bg-white/5 transition ${!notif.is_read ? 'bg-yellow-500/5' : ''}`}>
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${!notif.is_read ? 'text-yellow-400' : 'text-white'}`}>{notif.title}</p>
                    <p className="text-xs text-gray-300 mt-1 break-words">{notif.message}</p>
                    <p className="text-gray-500 text-[10px] mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {!notif.is_read && (
                      <button onClick={() => handleMarkRead(notif.id)} className="text-green-400 text-xs hover:underline">
                        {t('notifications.markRead')}
                      </button>
                    )}
                    <button onClick={() => handleDelete(notif.id)} className="text-red-400 text-xs hover:underline">
                      {t('notifications.delete')}
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-green-900 to-emerald-950 text-white font-cairo" dir={i18n.dir()}>
      <ToastMessage />
      <AnimatePresence>{showNotifications && isMobile && <NotificationsModal />}</AnimatePresence>
      <div className="sticky top-0 z-30 backdrop-blur-md bg-black/30 border-b border-white/10 px-4 sm:px-6 py-3">
        <div className="flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-2 sm:gap-3">
            <img
              src="/img/logo.png"
              alt="Soccer Up Logo"
              className="h-10 sm:h-12 md:h-14 w-auto object-contain drop-shadow-lg"
              onError={(e) => { e.target.onerror = null; e.target.src = '/img/player.png'; }}
            />
            <h1 className="text-xl sm:text-2xl md:text-3xl font-black tracking-tight bg-gradient-to-r from-yellow-300 to-yellow-500 bg-clip-text text-transparent">
              Soccer Up
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-black/40 rounded-full px-2 py-1 sm:px-3">
              <FaUserCircle className="text-sm sm:text-base" />
              <span className="text-xs sm:text-sm truncate max-w-[80px]">{playerName || t('common.guest')}</span>
            </div>
            <div className="flex items-center gap-1 bg-green-800/50 rounded-full px-2 py-1 sm:px-3">
              <FaGlobe className="text-sm sm:text-base" />
              <span className="text-xs sm:text-sm">{userContinent}</span>
            </div>
          </div>
          <div className="hidden md:flex gap-2 flex-wrap items-center text-sm">
            <button onClick={() => setShowStandings(true)} className="bg-green-600/80 hover:bg-green-600 px-2 py-1 sm:px-3 rounded-full text-xs sm:text-sm transition">{t('nav.standings')}</button>
            <button onClick={openTacticalView} className="bg-blue-600/80 hover:bg-blue-600 px-2 py-1 sm:px-3 rounded-full text-xs sm:text-sm transition">{t('nav.squad')}</button>
            <button onClick={() => setShowStatistics(true)} className="bg-purple-600/80 hover:bg-purple-600 px-2 py-1 sm:px-3 rounded-full text-xs sm:text-sm transition">{t('nav.stats')}</button>
            <button onClick={() => setShowProfile(true)} className="bg-indigo-600/80 hover:bg-indigo-600 px-2 py-1 sm:px-3 rounded-full text-xs sm:text-sm transition">{t('nav.profile')}</button>
            <button onClick={() => setShowHallOfFame(true)} className="bg-pink-600/80 hover:bg-pink-600 px-2 py-1 sm:px-3 rounded-full text-xs sm:text-sm transition">{t('nav.hallOfFame')}</button>
            <LanguageSelector />
            <div className="relative" ref={desktopNotifRef}>
              <button onClick={() => setShowNotifications(!showNotifications)} className="relative bg-gray-700/80 hover:bg-gray-600 p-1.5 sm:p-2 rounded-full transition">
                <FaBell className="text-yellow-400 text-sm sm:text-base" />
                {unreadCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] sm:text-xs rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">{unreadCount}</span>}
              </button>
              {showNotifications && !isMobile && (
                <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-80 sm:w-96 bg-gray-800 rounded-xl shadow-2xl border border-yellow-500/30 z-50 overflow-hidden`}>
                  <div className="flex justify-between items-center p-3 border-b border-white/10 bg-gray-800/90 sticky top-0">
                    <h3 className="font-bold text-white text-sm sm:text-base">{t('notifications.title')}</h3>
                    {unreadCount > 0 && <button onClick={handleMarkAllRead} className="text-xs text-yellow-400 hover:underline">{t('notifications.markAllRead')}</button>}
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifLoading ? (
                      <div className="p-4 text-center text-gray-400 text-sm">{t('common.loading')}</div>
                    ) : notifications.length === 0 ? (
                      <div className="p-4 text-center text-gray-400 text-sm">{t('notifications.noNotifications')}</div>
                    ) : (
                      notifications.filter(n => n && n.id).map(notif => (
                        <div key={notif.id} className={`p-3 border-b border-white/5 hover:bg-white/5 transition ${!notif.is_read ? 'bg-yellow-500/5' : ''}`}>
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-semibold truncate ${!notif.is_read ? 'text-yellow-400' : 'text-white'}`}>{notif.title}</p>
                              <p className="text-xs text-gray-300 mt-1 break-words">{notif.message}</p>
                              <p className="text-gray-500 text-[10px] mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              {!notif.is_read && (
                                <span onClick={() => handleMarkRead(notif.id)} className="text-green-400 text-xs hover:underline cursor-pointer px-1">✓</span>
                              )}
                              <span onClick={() => handleDelete(notif.id)} className="text-red-400 text-xs hover:underline cursor-pointer px-1">✕</span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <button onClick={openTutorial} className="bg-purple-600/80 hover:bg-purple-600 p-1.5 sm:p-2 rounded-full transition" title={t('nav.tutorial')}>
              <FaQuestionCircle className="text-white text-sm sm:text-base" />
            </button>
            <button onClick={onLogout} className="bg-gray-700/80 hover:bg-red-700 px-2 py-1 sm:px-3 rounded-full transition flex items-center gap-1 text-xs sm:text-sm">
              <FaSignOutAlt /> {t('nav.logout')}
            </button>
          </div>
          <div className="flex md:hidden items-center gap-2">
            <LanguageSelector />
            <button onClick={() => setShowNotifications(true)} className="relative bg-gray-700/80 hover:bg-gray-600 p-1.5 rounded-full">
              <FaBell className="text-yellow-400 text-sm" />
              {unreadCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{unreadCount}</span>}
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white text-2xl focus:outline-none p-1">
              <FaBars />
            </button>
          </div>
        </div>
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="md:hidden mt-3 pt-3 border-t border-white/10 flex flex-col gap-2"
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <button
                onClick={() => {
                  setShowStandings(true);
                  setMobileMenuOpen(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition ${
                  isRTL ? 'text-right' : 'text-left'
                }`}
              >
                {t('nav.standings')}
              </button>
              <button
                onClick={() => {
                  openTacticalView();
                  setMobileMenuOpen(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition ${
                  isRTL ? 'text-right' : 'text-left'
                }`}
              >
                {t('nav.squad')}
              </button>
              <button
                onClick={() => {
                  setShowStatistics(true);
                  setMobileMenuOpen(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition ${
                  isRTL ? 'text-right' : 'text-left'
                }`}
              >
                {t('nav.stats')}
              </button>
              <button
                onClick={() => {
                  setShowProfile(true);
                  setMobileMenuOpen(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition ${
                  isRTL ? 'text-right' : 'text-left'
                }`}
              >
                {t('nav.profile')}
              </button>
              <button
                onClick={() => {
                  setShowHallOfFame(true);
                  setMobileMenuOpen(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition ${
                  isRTL ? 'text-right' : 'text-left'
                }`}
              >
                {t('nav.hallOfFame')}
              </button>
              <button
                onClick={() => {
                  openTutorial();
                  setMobileMenuOpen(false);
                }}
                className={`w-full px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition flex items-center gap-2 ${
                  isRTL ? 'justify-end flex-row-reverse' : 'justify-start'
                }`}
              >
                <FaQuestionCircle className={isRTL ? 'order-2' : 'order-1'} />
                <span className={isRTL ? 'order-1' : 'order-2'}>{t('nav.tutorial')}</span>
              </button>
              <button
                onClick={() => {
                  onLogout();
                  setMobileMenuOpen(false);
                }}
                className={`w-full px-3 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 transition flex items-center gap-2 ${
                  isRTL ? 'justify-end flex-row-reverse' : 'justify-start'
                }`}
              >
                <FaSignOutAlt className={isRTL ? 'order-2' : 'order-1'} />
                <span className={isRTL ? 'order-1' : 'order-2'}>{t('nav.logout')}</span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <main className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <SeasonInfo competition="domestic" continent={userContinent} title={t('competition.domestic')} icon={<FaGlobe />} />
          <SeasonInfo competition="champions" continent={null} title={t('competition.champions')} icon={<FaTrophy />} />
          <SeasonInfo competition="europa" continent={null} title={t('competition.europa')} icon={<FaMedal />} />
        </div>
        {seasonEnded && (
          <div className="mb-6 bg-gradient-to-r from-red-600/20 to-red-700/20 border border-red-500/30 rounded-xl px-4 py-3 text-center text-red-400 shadow-sm">
            <span className="text-lg mr-2">⚠️</span>
            <span className="font-bold">{seasonEndMessage}</span>
            <span className="opacity-80 ml-2 text-sm">• {t('season.endedSub') || 'New season will start in a few hours.'}</span>
          </div>
        )}
        <div className="mb-10" id="competition-selector">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><FaUsers className="text-yellow-400" /> {t('competition.choose')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setSelectedCompetition('domestic')} className={`relative rounded-2xl p-4 backdrop-blur-md transition-all ${selectedCompetition === 'domestic' ? 'bg-gradient-to-br from-green-600 to-green-800 shadow-lg ring-2 ring-green-400' : 'bg-white/10 hover:bg-white/20'}`}>
              <div className="flex justify-between items-start"><div className="text-3xl"><FaGlobe /></div><div className="text-right"><div className="text-xs text-gray-300">{t('competition.matchesLeft')}</div><div className="text-2xl font-bold">{dailyMatches.domestic}/5</div></div></div>
              <div className="mt-2 font-bold text-xl">{t('competition.domestic')}</div>
              <div className="text-sm opacity-80">{userContinent}</div>
              <div className="text-[10px] text-gray-400 mt-1">
                {t('season.matchesPlayed')}: {seasonMatches.domestic}/100
              </div>
              {selectedCompetition === 'domestic' && <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-400 rounded-full flex items-center justify-center text-black text-xs font-bold">✓</div>}
            </motion.button>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setSelectedCompetition('champions')} disabled={!isQualifiedForChampions} className={`relative rounded-2xl p-4 backdrop-blur-md transition-all ${selectedCompetition === 'champions' ? 'bg-gradient-to-br from-yellow-600 to-yellow-800 shadow-lg ring-2 ring-yellow-400' : isQualifiedForChampions ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-800/50 opacity-50 cursor-not-allowed'}`}>
              <div className="flex justify-between items-start"><div className="text-3xl"><FaTrophy /></div><div className="text-right"><div className="text-xs text-gray-300">{t('competition.matchesLeft')}</div><div className="text-2xl font-bold">{dailyMatches.champions}/5</div></div></div>
              <div className="mt-2 font-bold text-xl">{t('competition.champions')}</div>
              <div className="text-sm flex items-center gap-1">{!isQualifiedForChampions ? <><FaLock className="text-xs" /> {t('competition.notQualified')}</> : <><FaCheckCircle className="text-green-400" /> {t('competition.qualified')}</>}</div>
              <div className="text-[10px] text-gray-400 mt-1">
                {t('season.matchesPlayed')}: {seasonMatches.champions}/100
              </div>
              {selectedCompetition === 'champions' && <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center text-black text-xs font-bold">✓</div>}
            </motion.button>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setSelectedCompetition('europa')} disabled={!isQualifiedForEuropa} className={`relative rounded-2xl p-4 backdrop-blur-md transition-all ${selectedCompetition === 'europa' ? 'bg-gradient-to-br from-blue-600 to-blue-800 shadow-lg ring-2 ring-blue-400' : isQualifiedForEuropa ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-800/50 opacity-50 cursor-not-allowed'}`}>
              <div className="flex justify-between items-start"><div className="text-3xl"><FaMedal /></div><div className="text-right"><div className="text-xs text-gray-300">{t('competition.matchesLeft')}</div><div className="text-2xl font-bold">{dailyMatches.europa}/5</div></div></div>
              <div className="mt-2 font-bold text-xl">{t('competition.europa')}</div>
              <div className="text-sm flex items-center gap-1">{!isQualifiedForEuropa ? <><FaLock className="text-xs" /> {t('competition.notQualified')}</> : <><FaCheckCircle className="text-green-400" /> {t('competition.qualified')}</>}</div>
              <div className="text-[10px] text-gray-400 mt-1">
                {t('season.matchesPlayed')}: {seasonMatches.europa}/100
              </div>
              {selectedCompetition === 'europa' && <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center text-black text-xs font-bold">✓</div>}
            </motion.button>
            <motion.button 
              whileHover={{ scale: 1.02 }} 
              whileTap={{ scale: 0.98 }} 
              onClick={() => setSelectedCompetition('friendly')} 
              disabled={seasonEnded || Object.values(seasonMatches).reduce((a,b) => a+b, 0) < 100}
              className={`relative rounded-2xl p-4 backdrop-blur-md transition-all ${
                selectedCompetition === 'friendly' 
                  ? 'bg-gradient-to-br from-purple-600 to-purple-800 shadow-lg ring-2 ring-purple-400' 
                  : Object.values(seasonMatches).reduce((a,b) => a+b, 0) >= 100 && !seasonEnded
                    ? 'bg-white/10 hover:bg-white/20' 
                    : 'bg-gray-800/50 opacity-50 cursor-not-allowed'
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="text-3xl"><FaHandshake /></div>
                <div className="text-right">
                  <div className="text-xs text-gray-300">{t('competition.matchesLeft')}</div>
                  <div className="text-2xl font-bold">{dailyMatches.friendly}/3</div>
                </div>
              </div>
              <div className="mt-2 font-bold text-xl">{t('competition.friendly')}</div>
              <div className="text-sm opacity-80">
                {seasonEnded ? t('season.endedCannotPlay') : Object.values(seasonMatches).reduce((a,b) => a+b, 0) >= 100 
                  ? t('competition.friendlyDesc') 
                  : `🔒 ${t('competition.friendlyLocked', { count: 100 - Object.values(seasonMatches).reduce((a,b) => a+b, 0) })}`}
              </div>
              {selectedCompetition === 'friendly' && (
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-purple-400 rounded-full flex items-center justify-center text-black text-xs font-bold">✓</div>
              )}
            </motion.button>
          </div>
        </div>
        <div className="mb-6 bg-gradient-to-r from-purple-900/40 to-indigo-900/40 rounded-2xl p-5 border border-purple-500/30">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-purple-500/30 p-2 rounded-full">
                <FaCalendarAlt className="text-purple-400 text-xl" />
              </div>
              <div>
                <p className="text-gray-300 text-sm font-medium">{t('season.seasonProgress')}</p>
                <p className="text-white text-sm">
                  <span className="font-bold text-purple-400">{seasonMatches[selectedCompetition] ?? 0}</span>
                  <span className="text-gray-400"> / 100 matches</span>
                </p>
              </div>
            </div>
            <div className="flex-1 min-w-[120px]">
              <div className="w-full bg-gray-700 rounded-full h-2.5 overflow-hidden">
                <motion.div 
                  className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, ((seasonMatches[selectedCompetition] ?? 0) / 100) * 100)}%` }}
                  transition={{ duration: 0.6 }}
                />
              </div>
              <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                <span>0</span>
                <span>
                  {(seasonMatches[selectedCompetition] ?? 0) >= 100 
                    ? t('season.seasonComplete') 
                    : t('season.remaining', { count: 100 - (seasonMatches[selectedCompetition] ?? 0) })}
                </span>
                <span>100</span>
              </div>
            </div>
            {(seasonMatches[selectedCompetition] ?? 0) >= 90 && (seasonMatches[selectedCompetition] ?? 0) < 100 && !seasonEnded && (
              <div className="bg-yellow-500/20 border border-yellow-500/30 px-3 py-1 rounded-full text-yellow-300 text-xs font-medium animate-pulse">
                {t('season.nearLimit')}
              </div>
            )}
            {(seasonMatches[selectedCompetition] ?? 0) >= 100 && !seasonEnded && (
              <div className="bg-red-500/20 border border-red-500/30 px-3 py-1 rounded-full text-red-300 text-xs font-medium">
                {t('season.seasonComplete')}
              </div>
            )}
            {seasonEnded && (
              <div className="bg-red-500/30 border border-red-500/50 px-3 py-1 rounded-full text-red-300 text-xs font-medium animate-pulse">
                ⏳ {t('season.endedSub')}
              </div>
            )}
          </div>
        </div>
        <div className="mb-10" id="playing-style-selector">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <FaExchangeAlt className="text-yellow-400" /> {t('match.playingStyle') || 'Playing Style'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Object.entries(PLAYING_STYLES).map(([key, style]) => {
              const styleName = t(`match.${key}`);
              const styleDesc = t(`match.playingStyleDesc${key.charAt(0).toUpperCase() + key.slice(1)}`) || style.description;
              return (
                <motion.button 
                  key={key} 
                  whileHover={{ scale: 1.02 }} 
                  whileTap={{ scale: 0.98 }} 
                  onClick={() => handlePlayingStyleChange(key)} 
                  className={`relative rounded-2xl p-5 text-center transition-all backdrop-blur-sm ${
                    playingStyle === key 
                      ? 'bg-gradient-to-br from-yellow-500/40 to-yellow-600/30 border-2 border-yellow-400 shadow-xl' 
                      : 'bg-white/5 border border-white/10 hover:bg-white/15'
                  }`}
                >
                  <div className="text-5xl mb-3">{style.icon}</div>
                  <h3 className="text-xl font-bold mb-1">{styleName}</h3>
                  <p className="text-xs text-gray-300">{styleDesc}</p>
                  {playingStyle === key && (
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-black font-bold text-xs">✓</div>
                  )}
                </motion.button>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 text-center mt-3">
            {t('match.playingStyleDesc') || 'Long balls increase attack but decrease defense; short passing gives balanced bonuses.'}
          </p>
        </div>
        <div className="mb-10" id="formations-section">
          <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <FaExchangeAlt className="text-yellow-400" /> {t('formation.strategy')}
            </h2>
            <button
              onClick={() => setShowFormationInfo(!showFormationInfo)}
              className="text-sm bg-white/10 hover:bg-white/20 px-3 py-1 rounded-full flex items-center gap-1 transition"
            >
              <FaInfoCircle /> {showFormationInfo ? t('common.hideDetails') : t('common.showDetails')}
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5">
            {formations.map((f) => (
              <motion.button
                key={f.id}
                whileHover={{ scale: 1.02, y: -3 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleFormationChange(f)}
                className={`relative rounded-2xl p-4 text-left transition-all backdrop-blur-sm ${
                  selectedFormation.id === f.id
                    ? 'bg-gradient-to-br from-yellow-500/30 to-yellow-600/20 border-2 border-yellow-400 shadow-xl'
                    : 'bg-white/5 border border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-3xl">{f.icon}</div>
                  <div className="text-xs font-mono bg-black/50 px-2 py-0.5 rounded-full">{f.short}</div>
                </div>
                <h3 className="text-lg font-bold">{t(f.nameKey)}</h3>
                <p className="text-xs text-gray-300 mb-2">{t(f.descKey)}</p>
                <div className="flex flex-wrap gap-1 text-[10px] font-mono">
                  {f.bonusesKeys.map((bonus, idx) => (
                    <span key={idx} className="bg-black/40 px-1.5 py-0.5 rounded-full text-yellow-300">
                      {bonus}
                    </span>
                  ))}
                </div>
                {selectedFormation.id === f.id && (
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-black font-bold text-xs">✓</div>
                )}
              </motion.button>
            ))}
          </div>
          {showFormationInfo && (
            <div className="mt-5 bg-black/60 rounded-xl p-5 border border-gray-700 text-sm space-y-3">
              <h4 className="font-bold text-yellow-400">📖 {t('formation.explanations')}</h4>
              {['counter', 'aggressive', 'traditionalAttack', 'traditionalDefense', 'highPress', 'advancedDefense'].map(key => (
                <div key={key} className="border-t border-gray-700 pt-2 first:border-t-0">
                  <p className="font-semibold text-white">{t(`formationExplanations.${key}.name`)}</p>
                  <p className="text-gray-300 text-xs whitespace-pre-line">{t(`formationExplanations.${key}.detailed`)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        {team.length > 0 && <TeamStrengthPanel baseStrength={baseStrengthRaw} formationPower={formationPowerRaw} totalStrength={totalStrengthRaw} captain={captain} captainBonus={captainBonusRaw} />}
        {team.length > 0 && (
          <StadiumPitchView team={team} formation={selectedFormation} isMobile={isMobile} />
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12 mt-8">
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 flex items-center gap-4">
            <div className="bg-yellow-500/30 p-3 rounded-full"><FaClock className="text-yellow-400 text-2xl" /></div>
            <div><p className="text-gray-300 text-sm">{t('competition.matchesLeftToday')}</p><p className="text-3xl font-bold text-yellow-400">{dailyMatches[selectedCompetition]}<span className="text-lg text-gray-400">/{selectedCompetition === 'friendly' ? 3 : 5}</span></p><p className="text-xs capitalize text-gray-400">{selectedCompetition === 'domestic' ? t('competition.domestic') : selectedCompetition === 'champions' ? t('competition.champions') : selectedCompetition === 'europa' ? t('competition.europa') : t('competition.friendly')}</p></div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-5">
            <div className="flex items-center gap-4"><div className="bg-blue-500/30 p-3 rounded-full"><FaChartLine className="text-blue-400 text-2xl" /></div><div><p className="text-gray-300 text-sm">{t('squadManager.avgStats')}</p><div className="flex flex-wrap gap-2 text-sm font-mono"><span>⚽ {teamAverage.attack}</span><span>🛡️ {teamAverage.defense}</span><span>⚡ {teamAverage.speed}</span><span>🎯 {teamAverage.accuracy}</span><span>💪 {teamAverage.strength}</span><span>🧠 {teamAverage.tactics}</span></div></div></div>
          </div>
          <div className="flex flex-col items-center justify-center gap-2">
            <button 
              id="find-match-btn" 
              onClick={findMatch} 
              disabled={
                searching || 
                dailyMatches[selectedCompetition] <= 0 || 
                (selectedCompetition !== 'friendly' && (seasonMatches[selectedCompetition] ?? 0) >= 100) || 
                seasonEnded || 
                (selectedCompetition !== 'domestic' && selectedCompetition !== 'friendly' && 
                  ((selectedCompetition === 'champions' && !isQualifiedForChampions) || 
                   (selectedCompetition === 'europa' && !isQualifiedForEuropa)))
              } 
              className={`w-full py-4 rounded-full font-bold text-lg shadow-xl transition-all transform hover:scale-105 flex items-center justify-center gap-2 ${
                (searching || 
                 dailyMatches[selectedCompetition] <= 0 || 
                 (selectedCompetition !== 'friendly' && (seasonMatches[selectedCompetition] ?? 0) >= 100) || 
                 seasonEnded || 
                 (selectedCompetition !== 'domestic' && selectedCompetition !== 'friendly' && 
                  ((selectedCompetition === 'champions' && !isQualifiedForChampions) || 
                   (selectedCompetition === 'europa' && !isQualifiedForEuropa)))
                ) 
                  ? 'bg-gray-600 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-red-600 to-red-700 hover:shadow-red-500/30'
              }`}
            >
              {searching ? <>{waitingMsg}{showTimer && <span className="ml-2 text-yellow-300">({searchTimer}s)</span>}</> : seasonEnded ? '⏳ Season Ended - Wait for New Season' : <><FaArrowRight /> {t('match.findMatch')}</>}
            </button>
            {searching && <button onClick={cancelSearch} className="text-sm bg-gray-700 hover:bg-gray-600 px-5 py-1 rounded-full transition">{t('common.cancel')}</button>}
            {selectedCompetition !== 'friendly' && (seasonMatches[selectedCompetition] ?? 0) >= 100 && !seasonEnded && (
              <p className="text-red-400 text-xs mt-1">{t('season.seasonLimitReached')}</p>
            )}
            {seasonEnded && (
              <p className="text-yellow-400 text-xs mt-1 animate-pulse">⏳ {t('season.endedCannotPlay')}</p>
            )}
          </div>
        </div>
      </main>
      <TutorialManager userId={userId} onComplete={() => setShowTutorial(false)} forceShow={showTutorial} />
      <AnimatePresence>{showAdminPanel && <AdminDashboard onClose={() => setShowAdminPanel(false)} />}</AnimatePresence>
    </div>
  );
};

const AdminDashboard = ({ onClose }) => {
  const [adminKey, setAdminKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedContinent, setSelectedContinent] = useState('Europe');
  const handleReset = async (type) => {
    if (!adminKey.trim()) { setMessage('Please enter admin key'); return; }
    setLoading(true); setMessage('');
    try {
      let response;
      if (type === 'domestic') response = await resetDomesticSeason(selectedContinent, adminKey);
      else if (type === 'champions') response = await resetContinentalSeason('champions', adminKey);
      else response = await resetContinentalSeason('europa', adminKey);
      setMessage(`✅ Success: ${response.data.message || 'Reset completed'}`);
    } catch (err) {
      // ✅ NEW: تسجيل الخطأ
      await reportError(err, {
        component: 'AdminDashboard.handleReset',
        type,
        selectedContinent,
      });
      setMessage(`❌ Error: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl p-6 max-w-md w-full border border-yellow-500/30 shadow-2xl">
        <h2 className="text-2xl font-bold text-yellow-400 mb-4">🛠️ Admin Panel</h2>
        <div className="space-y-4">
          <div><label className="block text-sm text-gray-400 mb-1">Admin Key</label><input type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)} className="w-full p-2 rounded bg-gray-800 text-white border border-gray-700 focus:outline-none focus:border-yellow-500" placeholder="Enter admin key" /></div>
          <div><label className="block text-sm text-gray-400 mb-1">Continent (for domestic)</label><select value={selectedContinent} onChange={(e) => setSelectedContinent(e.target.value)} className="w-full p-2 rounded bg-gray-800 text-white border border-gray-700"><option value="Europe">Europe</option><option value="Asia">Asia</option><option value="Africa">Africa</option><option value="Americas">Americas</option></select></div>
          <div className="flex gap-3 flex-wrap"><button onClick={() => handleReset('domestic')} disabled={loading} className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-full text-sm font-bold">Reset Domestic</button><button onClick={() => handleReset('champions')} disabled={loading} className="bg-yellow-600 hover:bg-yellow-700 px-4 py-2 rounded-full text-sm font-bold">Reset Champions</button><button onClick={() => handleReset('europa')} disabled={loading} className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-full text-sm font-bold">Reset Europa</button></div>
          {message && <div className={`p-2 rounded text-sm ${message.startsWith('✅') ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>{message}</div>}
          <button onClick={onClose} className="w-full bg-gray-700 hover:bg-gray-600 py-2 rounded-full mt-2">Close</button>
        </div>
      </div>
    </div>
  );
};

export default MainGame;